import React, { useState, useLayoutEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'bootstrap/dist/css/bootstrap.min.css';
import HighchartsExporting from 'highcharts/modules/exporting';
import { useTranslate } from "../../hooks/LanguageProvider";
import { ClipLoader } from 'react-spinners';
import '../../assets/css/fleet-activity.css';



const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface DataPoint {
  TIMESTAMP: string;
  TEMPS: string; // Remplacez par le type réel
  SOG: number;   // Remplacez par le type réel
  EtatM: number;  // Remplacez par le type réel
  cumulativeDifference: number;
}
interface StatsResult {
  maxVitesse: number;
  moyVitesse: string;
  distance: number | string;
}

HighchartsExporting(Highcharts);


const LineChartComponent: React.FC<{ psnDispositif: string }> = ({ psnDispositif }) => {
  const [lineChartData, setLineChartData] = useState<DataPoint[]>([] ?? []);
  const [statsResult, setStatsResult] = useState<StatsResult>({ maxVitesse: 0, moyVitesse: "", distance: 0 });
  const [tempsMarche, setTempsMarche] = useState<{ heures: number, minutes: number, secondes: number } | null>(null);
  const [tempsArret, setTempsArret] = useState<{ heures: number, minutes: number, secondes: number } | null>(null);
  const { translate } = useTranslate();
  const [loading, setLoading] = useState(true);

  const formatDistance = (value: number | string | null | undefined) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '--';
  };

  const chartPoints = lineChartData
    .map((dataPoint) => ({
      dataPoint,
      timestamp: new Date(dataPoint.TIMESTAMP).getTime(),
    }))
    .filter(({ timestamp }) => Number.isFinite(timestamp));

  const cumulativeDifferenceSeries = {
    name: translate('Distance'),
    data: chartPoints.map(({ dataPoint, timestamp }) => ({
      x: timestamp,
      y: dataPoint.cumulativeDifference,
    })),
    color: 'blue',
    yAxis: 2, // Utilisez un nouvel axe y pour cette série
    type: 'line',
    cursor: 'pointer',
    connectNulls: false,
    tooltip: {
      valueDecimals: 2,
      valueSuffix: ' km',
    },
  };

  useLayoutEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const currentDate = new Date();
        const dateD = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0, 0).toISOString();
        const dateF = currentDate.toISOString();
        const response = await fetch(`${backendUrl}/api/calculateStats`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dateD: dateD,
            dateF: dateF,
            PSN: psnDispositif,
          }),
        });

        if (response.ok) {
          const data = await response.json();

          setLineChartData(data.lineChart);
          setStatsResult(data.statsResult);
          setTempsMarche(data.tempsMarche);
          setTempsArret(data.tempsArret);
        } else {
          console.error('La requête a échoué avec le statut:', response.status);
        }
      } catch (error) {
        console.error('Une erreur s\'est produite lors de la récupération des données de l\'API:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData(); // Appel de la fonction asynchrone à l'intérieur de useLayoutEffect
  }, [psnDispositif]);




  const chartOptions = {
    chart: {
      type: 'line',
      zoomType: 'x',
      height: 420,
      spacing: [16, 16, 12, 16],
      reflow: true,
    },
    title: {
      text: translate('Diagramme de distance, vitesse et contact'),
    },
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
            'downloadSVG',
            'downloadCSV',
            'downloadXLS',
          ],
        },
      },
    },

    xAxis: {
      type: 'datetime',
      crosshair: true,
      dateTimeLabelFormats: {
        second: '%H:%M:%S',
        minute: '%H:%M:%S',
        hour: '%H:%M:%S',
      },
    },
    tooltip: {
      shared: true,
      split: false,
      crosshairs: true,
      xDateFormat: '%H:%M:%S',
      headerFormat: '<b>{point.key:%H:%M:%S}</b><br/>',
    },

    series: [
      {
        name: translate('Vitesse'),
        data: chartPoints.map(({ dataPoint, timestamp }) => ({
          x: timestamp,
          y: dataPoint.SOG,
        })),
        color: 'green',
        cursor: 'pointer',
        connectNulls: false,


      },
      {
        name: translate('Contact'),
        data: chartPoints.map(({ dataPoint, timestamp }) => ({
          x: timestamp,
          y: dataPoint.EtatM,
        })),
        yAxis: 1, // Associer la série à l'axe y secondaire
        color: 'red',
        cursor: 'pointer',
        connectNulls: false,

      },

    ],


    yAxis: [
      {
        title: {
          text: translate('Vitesse'),
          color: 'green',
        },
      },
      {
        title: {
          text: translate('Contact'),
          color: 'red',
        },
        opposite: true,
      },
      {
        title: {
          text: translate('Distance'),
          color: 'blue',
        },
        opposite: true,
        labels: {
          format: '{value:.2f}',
        },
      },
    ],
  };
  const renderMaxSpeedStatus = () => {
    if (statsResult.maxVitesse > 90) {
      return (
        <>
          <i className="las la-tachometer-alt" style={{ color: "red" }}></i>
          <span style={{ color: "red" }}> {translate("Excès de vitesse")}</span>
        </>
      );
    } else {
      return (
        <>
          <i className="las la-tachometer-alt" style={{ color: "green" }}></i>
          <span style={{ color: "green" }}>
            {" "}
            {translate("Pas d'excès de vitesse")}
          </span>
        </>
      );
    }
  };

  chartOptions.series.push(cumulativeDifferenceSeries);
  return (
    <div className="gps-activity">
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <ClipLoader size={50} color={'#123abc'} loading={loading} />
        </div>
      )}
      {!loading && (
        <div className="row g-3 align-items-stretch">
          <div className="col-xl-3 col-lg-4 col-md-5 col-sm-12">
            <div className="gps-statistics-card h-100">
              <div className="gps-statistics-card__heading">
                <span className="gps-statistics-card__icon"><i className="las la-chart-line"></i></span>
                <h2>{translate("Statistiques")}</h2>
              </div>

              <div className="gps-statistics-card__speed-grid">
                <div className="gps-statistic-value">
                  <span>{translate("Max")} {translate("Vitesse")}</span>
                  <strong>{statsResult.maxVitesse} <small>{translate("km/h")}</small></strong>
                </div>
                <div className="gps-statistic-value">
                  <span>{translate("Avg")} {translate("Vitesse")}</span>
                  <strong>{statsResult.moyVitesse} <small>{translate("km/h")}</small></strong>
                </div>
              </div>

              <div className={`gps-speed-status ${statsResult.maxVitesse > 90 ? "is-danger" : "is-safe"}`}>
                {renderMaxSpeedStatus()}
              </div>

              <div className="gps-statistics-card__list">
                <div className="gps-statistic-row">
                  <span className="gps-statistic-row__icon"><i className="las la-route"></i></span>
                  <span className="gps-statistic-row__content">
                    <span>{translate("Distance")}</span>
                    <strong>{formatDistance(statsResult.distance)} {translate("km")}</strong>
                  </span>
                </div>
              {tempsMarche && (
                  <div className="gps-statistic-row">
                    <span className="gps-statistic-row__icon"><i className="las la-truck-moving"></i></span>
                    <span className="gps-statistic-row__content">
                      <span>{translate("Driving time")}</span>
                      <strong>{tempsMarche.heures}h {tempsMarche.minutes}m {tempsMarche.secondes}s</strong>
                    </span>
                  </div>
              )}

              {tempsArret && (
                  <div className="gps-statistic-row">
                    <span className="gps-statistic-row__icon"><i className="las la-parking"></i></span>
                    <span className="gps-statistic-row__content">
                      <span>{translate("Parking duration")}</span>
                      <strong>{tempsArret.heures}h {tempsArret.minutes}m {tempsArret.secondes}s</strong>
                    </span>
                  </div>
              )}
              </div>
            </div>
          </div>
          <div className="col-xl-9 col-lg-8 col-md-7 col-sm-12 gps-activity__chart">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
        </div>
      )}
    </div>




  );
};
export default LineChartComponent;
